import { useState, type SVGProps } from "react";
import { useNavigate } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import {
  Avatar,
  AvatarFallback,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Heading,
  Text,
} from "@lendcord/concord";
import { getInitials } from "@/app/initials";
import { useSession } from "@/app/session";
import { useLogoutMutation } from "@/auth/api/auth.api";
import { useGetProfileQuery } from "@/app/profile/api/profile.api";
import { getFullName } from "@/app/profile/fullName";
import { ChangePasswordDialog } from "@/features/change-password";

function MenuIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" aria-hidden="true" {...props}>
      <path d="M3 6h18M3 12h18M3 18h18" />
    </svg>
  );
}

interface TopBarProps {
  titleId: string;
  onToggleSidebar: () => void;
}

export function TopBar({ titleId, onToggleSidebar }: TopBarProps) {
  const intl = useIntl();
  const navigate = useNavigate();
  const { data: profile } = useGetProfileQuery();
  const fullName = getFullName(profile ?? null);
  const { logout } = useSession();
  const [logoutRequest] = useLogoutMutation();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);

  async function handleLogout() {
    setIsLoggingOut(true);
    try {
      await logoutRequest().unwrap();
    } catch {
      // Ignored — the local session still ends via `finally` below
      // regardless of whether server-side revocation succeeded (e.g. an
      // already-expired token shouldn't trap the user on this page).
    } finally {
      logout();
      navigate("/auth", { replace: true });
    }
  }

  return (
    <header className="flex items-center justify-between border-b border-border bg-surface-raised px-4 py-4 sm:px-6">
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={onToggleSidebar}
          aria-label={intl.formatMessage({ id: "lender.topBar.menuButtonLabel" })}
          className="text-text-muted hover:text-text"
        >
          <MenuIcon className="h-6 w-6" />
        </button>
        <Heading level={1}>
          <FormattedMessage id={titleId} />
        </Heading>
      </div>

      <div className="flex items-center gap-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={intl.formatMessage({ id: "lender.topBar.accountMenuLabel" })}
              className="flex items-center gap-2 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/40"
            >
              <Text size="sm" variant="muted" className="hidden sm:block">
                {fullName ?? "…"}
              </Text>
              <Avatar size="sm" className="bg-accent-600">
                <AvatarFallback className="text-xs font-bold text-white">
                  {fullName ? getInitials(fullName) : ""}
                </AvatarFallback>
              </Avatar>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => setIsChangePasswordOpen(true)}>
              <FormattedMessage id="lender.topBar.changePasswordMenuItem" />
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => navigate("/profile")}>
              <FormattedMessage id="lender.topBar.profileMenuItem" />
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={handleLogout} disabled={isLoggingOut}>
              <FormattedMessage
                id={isLoggingOut ? "lender.topBar.loggingOutMenuItem" : "lender.topBar.logoutMenuItem"}
              />
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <ChangePasswordDialog open={isChangePasswordOpen} onOpenChange={setIsChangePasswordOpen} />
    </header>
  );
}
